/**
 * devAuthService.ts — Phase 3 (gap-closing) helper.
 *
 * Replaces the legacy `'DEV_TOKEN'` magic-string client path. Calls
 * POST /api/auth/dev/issue (env-class=dev gated server-side) and
 * returns the JWT. The token is shape-compatible with the standard
 * Twitch JWT flow — same Bearer header, same identity resolution,
 * same Colyseus join.options.token slot.
 *
 * On a deployed tier (H2000_ENV !== dev), the endpoint returns 410
 * Gone. Callers handle the null return as "skip-login isn't
 * available here; user must Twitch-auth instead."
 *
 * Local cache: the issued JWT is stored in localStorage under
 * `helix_jwt` (same key used by every other auth path), so a hot
 * reload doesn't re-issue. The TTL on the JWT itself (24h) bounds
 * cache freshness.
 */

interface IssueDevTokenOptions {
  characterClass?: string;
  characterName?: string;
}

interface IssueDevTokenResult {
  token: string;
  userId: string;
  expiresIn: number;
}

export async function issueDevToken(opts: IssueDevTokenOptions = {}): Promise<IssueDevTokenResult | null> {
  try {
    const res = await fetch('/api/auth/dev/issue', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(opts),
      credentials: 'same-origin',
    });
    if (!res.ok) {
      // 410 Gone → endpoint disabled on this tier; caller falls back.
      return null;
    }
    const body = await res.json();
    if (!body?.token || !body?.userId) return null;
    return { token: body.token, userId: body.userId, expiresIn: body.expiresIn };
  } catch {
    return null;
  }
}
